import * as React from "react";
import {useEffect, useState} from "react";
import {withVerifyAccess} from "../../common/withVerifyAccess";
import {UserTypeEnum} from "../../../model/UserInfo.model";
import {LoginContentAdminApi} from "../../../api/LoginContentAdminApi";
import {LoadingSpinner} from "../../icons/LoadingSpinner.component";
import {AdminLoginContentRegionTabs} from "./AdminLoginContentRegionTabs";
import {AdminLoginContentAddRegion} from "./AdminLoginContentAddRegion";
import Toast from "../../common/Toast.component";
import {IToastNotification} from "../../../utils/planUserRemoval";
import {RaisedButton} from "../../common/RaisedButton";
import {dangerOutlineButtonStyle} from "../../common/buttonStyles";
import {DialogComponent} from "../../common/Dialog.component";
import {AdminLoginRegionContentTabs, RegionContentTab} from "./AdminLoginRegionContentTabs";
import {AdminLoginStatContent} from "./AdminLoginStatContent";
import {AdminLoginServicePillContent} from "./AdminLoginServicePillContent";
import {AdminLoginCarouselCardsContent} from "./AdminLoginCarouselCardsContent";
import {AdminLoginCountriesContent} from "./AdminLoginCountriesContent";
import {AdminLoginHeroContent} from "./AdminLoginHeroContent";
import {AdminLoginDraftContentBanner} from "./AdminLoginDraftContentBanner";
import {AdminLoginPublishedBanner} from "./AdminLoginPublishedBanner";
import {AdminLoginContentUnpublishedSummary} from "./AdminLoginContentUnpublishedSummary";
import {AdminRegion} from "../../../model/RegionLoginContent.model";

export const ADMIN_LOGIN_CONTENT_HEADER = "Login Page Content";
export const ADMIN_LOGIN_CONTENT_PUBLISH_SUCCESS_TOAST = "Region published.";
export const ADMIN_LOGIN_CONTENT_PUBLISH_FAILURE_TOAST = "Failed to publish region.";
export const ADMIN_LOGIN_CONTENT_UNPUBLISH_SUCCESS_TOAST = "Region unpublished - reverted to draft";
export const ADMIN_LOGIN_CONTENT_UNPUBLISH_FAILURE_TOAST = "Failed to unpublish region.";
export const ADMIN_LOGIN_CONTENT_UNPUBLISH_DESCRIPTION =
    "Unpublishing <<<regionName>>> will remove it from the login page. Users from this region will see the Default content instead. Continue?";
export const ADMIN_LOGIN_CONTENT_SUB_HEADER = "Manage marketing content shown on the login page, per region";
export const ADMIN_LOGIN_CONTENT_DEFAULT_REGION_TEXT1 = "Default is the global fallback";
export const ADMIN_LOGIN_CONTENT_DEFAULT_REGION_TEXT2 = "Users from countries not assigned to another region will see this Default content.";
export const ADMIN_LOGIN_CONTENT_DELETE_DESCRIPTION = "Delete region <<<regionName>>> and all its content? This cannot be undone.";

const AdminLoginContentPage: React.FunctionComponent = () => {
    const [regions, setRegions] = useState<AdminRegion[]>([]);
    const [activeRegion, setActiveRegion] = useState<AdminRegion | null>(null);
    const [loading, setLoading] = useState(true);
    const [showAddRegion, setShowAddRegion] = useState(false);
    const [toast, setToast] = useState<IToastNotification | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
    const [activeSection, setActiveSection] = useState<RegionContentTab>(RegionContentTab.HERO);

    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(null), 3000);
        return () => clearTimeout(timer);
    }, [toast]);

    useEffect(() => {
        LoginContentAdminApi.getRegions().then((response) => {
            setRegions(response);
            if (!activeRegion) {
                setActiveRegion(() => {
                    return response.length > 0 ? response[0] : null;
                });
            }
            setLoading(false);
        });
    }, [activeRegion]);

    const handleContentSaved = (regionId: number) => {
        LoginContentAdminApi.getRegions().then((response) => {
            setRegions(response);
            setActiveRegion(prev =>
                prev && prev.id === regionId ? (response.find(r => r.id === regionId) ?? prev) : prev);
        });
    };

    const handlePublish = (regionId: number) => {
        LoginContentAdminApi.publishRegion(regionId)
            .then(() => {
                setToast({message: ADMIN_LOGIN_CONTENT_PUBLISH_SUCCESS_TOAST, type: "success"});
                handleContentSaved(regionId);
            })
            .catch(() => {
                setToast({message: ADMIN_LOGIN_CONTENT_PUBLISH_FAILURE_TOAST, type: "error"});
            });
    };

    const handleConfirmUnpublish = () => {
        if (!activeRegion || activeRegion.isDefault) return;
        const regionId = activeRegion.id;
        LoginContentAdminApi.unpublishRegion(regionId)
            .then(() => {
                setShowUnpublishConfirm(false);
                setToast({message: ADMIN_LOGIN_CONTENT_UNPUBLISH_SUCCESS_TOAST, type: "success"});
                handleContentSaved(regionId);
            })
            .catch(() => {
                setShowUnpublishConfirm(false);
                setToast({message: ADMIN_LOGIN_CONTENT_UNPUBLISH_FAILURE_TOAST, type: "error"});
            });
    };

    const handleConfirmDelete = () => {
        if (!activeRegion || activeRegion.isDefault) return;
        LoginContentAdminApi.deleteRegion(activeRegion.id)
            .then(() => {
                setShowDeleteConfirm(false);
                setActiveRegion(null);
                setToast({ message: "Region deleted", type: "success" });
            })
            .catch(() => {
                setShowDeleteConfirm(false);
                setToast({ message: "Failed to delete region", type: "error" });
            });
    };

    if (loading) {
        return <LoadingSpinner/>;
    }

    const renderHeader = () => {
        return <div className="admin-login-content__header">
            <div>
                <h1 className="admin-login-content__title">{ADMIN_LOGIN_CONTENT_HEADER}</h1>
                <p className="admin-login-content__subtitle">{ADMIN_LOGIN_CONTENT_SUB_HEADER}</p>
            </div>
        </div>;
    };

    const renderDefaultContent = () => {
        return <div className="admin-login-content__default-info">
            <i className="fa-solid fa-circle-info" />
            <div>
                <strong>{ADMIN_LOGIN_CONTENT_DEFAULT_REGION_TEXT1}</strong>
                <p>{ADMIN_LOGIN_CONTENT_DEFAULT_REGION_TEXT2}</p>
            </div>
        </div>;
    };

    const renderSelectedRegion = (isDefaultRegion: boolean) => {
        return <div className="admin-login-content__card">
            <AdminLoginRegionContentTabs
                activeRegion={activeRegion as AdminRegion}
                activeSection={activeSection}
                onSectionChange={setActiveSection} />
            <div
                data-testid="admin-login-stat-content-section"
                style={{display: activeSection === RegionContentTab.STATS ? "block" : "none"}}>
                <AdminLoginStatContent
                    activeRegion={activeRegion as AdminRegion}
                    setToast={setToast}
                    onStatsSaved={handleContentSaved}/>
            </div>
            <div
                data-testid="admin-login-service-pill-content-section"
                style={{display: activeSection === RegionContentTab.PILLS ? "block" : "none"}}>
                <AdminLoginServicePillContent
                    activeRegion={activeRegion as AdminRegion}
                    setToast={setToast}
                    onPillsSaved={handleContentSaved}/>
            </div>
            <div
                data-testid="admin-login-carousel-content-section"
                style={{display: activeSection === RegionContentTab.CAROUSEL ? "block" : "none"}}>
                <AdminLoginCarouselCardsContent
                    activeRegion={activeRegion as AdminRegion}
                    setToast={setToast}
                    onCarouselSaved={handleContentSaved}/>
            </div>
            <div
                data-testid="admin-login-countries-content-section"
                style={{display: activeSection === RegionContentTab.COUNTRIES ? "block" : "none"}}>
                <AdminLoginCountriesContent
                    regions={regions}
                    activeRegion={activeRegion as AdminRegion}
                    setToast={setToast}
                    onCountriesSaved={handleContentSaved}/>
            </div>
            <div
                data-testid="admin-login-hero-content-section"
                style={{display: activeSection === RegionContentTab.HERO ? "block" : "none"}}>
                <AdminLoginHeroContent
                    activeRegion={activeRegion as AdminRegion}
                    setToast={setToast}
                    onHeroSave={handleContentSaved}/>
            </div>
            {!isDefaultRegion && (
                <div className="admin-login-content__footer">
                    <RaisedButton
                        type="button"
                        className="admin-login-content__btn admin-login-content__btn--danger"
                        style={dangerOutlineButtonStyle}
                        primary={false}
                        onClick={() => setShowDeleteConfirm(true)}
                    >
                        <i className="fa-solid fa-trash" /> Delete Region
                    </RaisedButton>
                </div>
            )}
        </div>;
    };

    const renderToast = () => {
        return <Toast visible={!!toast} message={toast?.message || ""} type={toast?.type || "success"} />;
    };

    const renderPublishBanner = (region: AdminRegion) => {
        if (region.hasDraft) {
            return <AdminLoginDraftContentBanner region={region} onPublish={handlePublish} />;
        }

        if (region.published) {
            return <AdminLoginPublishedBanner region={region} onUnpublish={() => setShowUnpublishConfirm(true)} />;
        }

        return null;
    };

    return (
        <div className="admin-login-content" data-testid="admin-login-content">
            <div className="admin-login-content__container">
                {renderHeader()}
                <AdminLoginContentUnpublishedSummary
                    regions={regions}
                    onRegionSelect={setActiveRegion}
                />
                <AdminLoginContentRegionTabs
                    regions={regions}
                    activeRegionId={activeRegion?.id ?? undefined}
                    onRegionSelect={setActiveRegion}
                    setShowAddRegion={() => setShowAddRegion(!showAddRegion)}
                />
                {showAddRegion && <AdminLoginContentAddRegion
                        regions={regions}
                        setShowAddRegion={setShowAddRegion}
                        setActiveRegion={setActiveRegion}
                        setToast={setToast}/>
                }
                {activeRegion && renderPublishBanner(activeRegion)}
                {activeRegion?.isDefault && renderDefaultContent()}
                {activeRegion && renderSelectedRegion(activeRegion.isDefault)}
                {toast && renderToast()}
                <DialogComponent
                    open={showDeleteConfirm}
                    title="Delete Region"
                    description={ADMIN_LOGIN_CONTENT_DELETE_DESCRIPTION.replace("<<<regionName>>>", activeRegion?.regionName ?? "")}
                    yesText="Delete"
                    noText="Cancel"
                    onYes={() => void handleConfirmDelete()}
                    onNo={() => setShowDeleteConfirm(false)}
                />
                <DialogComponent
                    open={showUnpublishConfirm}
                    title="Unpublish Region"
                    description={ADMIN_LOGIN_CONTENT_UNPUBLISH_DESCRIPTION.replace("<<<regionName>>>", activeRegion?.regionName ?? "")}
                    yesText="Unpublish"
                    noText="Cancel"
                    onYes={() => void handleConfirmUnpublish()}
                    onNo={() => setShowUnpublishConfirm(false)}
                />
            </div>
        </div>
    );
};

export default withVerifyAccess(AdminLoginContentPage, UserTypeEnum.ADMIN);
